import React from 'react';
import type { BaseWidget, WidgetParams } from '@/types/chatbot.types';
import HeaderWidget from './HeaderWidget';
import LinkButtonsWidget from './LinkButtonsWidget';
import TextWidget from './TextWidget';
import SpaceLineWidget from './SpaceLineWidget';
import InsightResponseWidget from './InsightResponseWidget';

/**
 * Widget registry maps widget types to their React components
 */
export const widgetRegistry: Record<string, React.ComponentType<WidgetParams>> = {
  Header: HeaderWidget,
  LinkButtons: LinkButtonsWidget,
  Text: TextWidget,
  SpaceLine: SpaceLineWidget,
  Insight: InsightResponseWidget,
};

/**
 * Render a widget based on its type
 */
export const renderWidget = (
  widget: BaseWidget,
  index: number,
  shouldAnimate = true,
  onAnimationComplete?: () => void,
  isStreaming?: boolean,
  onPublishClick?: (insightId: string) => void,
): React.ReactElement | null => {
  const WidgetComponent = widgetRegistry[widget.type];
  if (!WidgetComponent) {
    console.warn(`Unknown widget type: ${widget.type}`);
    return null;
  }

  // Spread params as props to the component, with animation control
  const props = {
    ...(widget.params || {}),
    shouldStart: shouldAnimate,
    onAnimationComplete,
    // Pass isStreaming to TextWidget and structured response widgets for clean animation control
    ...((widget.type === 'Text' || widget.type === 'Insight') && {
      isStreaming,
    }),
    // Pass onPublishClick to Insight widgets
    ...(widget.type === 'Insight' && {
      onPublishClick,
    }),
  };

  return <WidgetComponent {...props} />;
};
